import { IModal } from 'customTypes';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import TuiGrid from '../Table/TuiGrid';
import { BtnBlue, BtnGray } from '../Button';
import { useTranslation } from 'react-i18next';
import { Modal, ModalBackground } from '../../assets/styles/Modal';
import i18n from '../../translation/i18n';
import { PulseLoader } from 'react-spinners';
import styled from 'styled-components';
import useTuiGridSize from '../../hooks/useTuiGridSize';

interface StyleProps {
  minWidth: number;
}

const Root = styled.div<StyleProps>`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 5010;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 60%);
  backdrop-filter: blur(0.5rem);
  user-select: none;

  > .modal {
    position: relative;
    max-width: ${(prop: StyleProps) => prop.minWidth}rem;
    width: 100%;
    border-radius: 0.5rem;
    overflow: hidden;
    height: 100%;
    max-height: calc(100vh - 9.75rem);
    // max-height: calc(100dvh - 2rem);

    > .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      background-color: var(--filled-blue-deep);
      color: var(--text-reverse);
      padding: 0.5rem;
      > .title {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0 0.5rem;
      }
      > .closeButton {
        border-radius: 0.5rem;
        margin: 0.375rem;
        padding: 0.25rem;
        display: flex;
        cursor: pointer;
        > .material-symbols-rounded {
          font-variation-settings: 'FILL' 0, 'wght' 600, 'GRAD' 0, 'opsz' 24;
          font-size: 1.375rem;
          color: var(--primary);
        }
        &:hover {
          background-color: var(--primary);
          box-shadow: 0 0.25rem 0.75rem 0 rgba(0, 0, 0, 0.1);
        }
      }
    }
    > .body {
      background-color: var(--board);
      display: flex;
      align-items: flex-start;
      height: 100%;
      // max-height: calc(100vh - 9.75rem);
      // max-height: calc(100dvh - 9.75rem);
      overflow: hidden;
      position: relative;
      padding: 0 calc(1rem - 17px) 0 1rem;
      > .content-container {
        max-height: inherit;
        overflow: auto;
        -webkit-overflow-scrolling: touch; /* iOS 기기에서 스크롤 성능 개선 */
        flex: 1 0 60%;
        display: flex;
        flex-direction: column;
        border-left: 1px solid var(--outline);
        background-color: var(--board);
      }

      .detect-tag {
        display: inline-block;
        padding: 2px 6px;
        font-size: 0.75rem;
        border-radius: 0.25rem;
        color: #fff;

        &.fireAudio {
          background-color: #db1919;
        }

        &.helmatAudio {
          background-color: #8e35dc;
        }
      }

      .tui-grid-container {
        height: calc(100% - 115px);
      }
    }
    > .footer {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      z-index: 100;
      display: flex;
      justify-content: flex-end;
      background-color: var(--board);
      border-top: 1px solid var(--outline);
      padding: 0.5rem;
      > .buttons {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        > .label {
          font-size: 0.875rem;
          letter-spacing: normal;
          color: var(--text-tertiary);
          padding: 0 0.5rem;
        }
      }
    }
  }
`;

interface IProps {
  openModal: IModal;
  setOpenModal: Dispatch<SetStateAction<IModal>>;
  minWidth?: number;
  title: string; // 모달창 제목
  columns?: any; // 테이블 헤더 컬럼
  tableState?: any; // 테이블 리스트
  setChkList?: any; // 체크리스트
  handleSave: any; // 저장 및 전송
  handleClose: any; // 모달창 닫기
  comparisonChkList?: any[]; // 테이블 리스트와 비교하여 있는 대상일 경우 체크
  loading: boolean;
}

const TableInfoMoal = ({ openModal, setOpenModal, minWidth = 64, title, columns, tableState, setChkList, handleSave, handleClose, comparisonChkList, loading }: IProps) => {
  const { t } = useTranslation();
  const [modalColumns, setModalColumns] = useState<any[]>([]);

  const componentRef = useRef<HTMLDivElement>(null);
  const { tuiHeight } = useTuiGridSize(componentRef);

  useEffect(() => {
    setModalColumns(columns);
  }, [i18n.language, columns]);

  const handleCheck = (rowKeyList: any) => {
    if (openModal.type === 'musicAdd') {
      const newArray = rowKeyList.map((item: any) => {
        return {
          bsfCd: item.bsfCd,
          bsfNameFormat: item.bsfNameFormat,
          bsfDuration: item.bsfDuration,
          bsfFsizeMb: item.bsfFsizeMb,
          format: item.format,
          bsfFurl: item.bsfFurl,
        };
      });
      setChkList(newArray);
    } else {
      setChkList(rowKeyList);
    }
  };

  return (
    <Root onClick={handleClose} minWidth={minWidth}>
      <div
        className='modal height-fit'
        role='presentation'
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <div className='header'>
          <div className='title'>{title}</div>
          <div className='closeButton' onClick={handleClose} role='presentation'>
            <span className='material-symbols-rounded'>close</span>
          </div>
        </div>

        <div className='body'>
          {loading ? (
            <div className='centered-content h600'>
              <PulseLoader color='rgb(0, 122, 255)' size='10px' />
            </div>
          ) : (
            <div ref={componentRef} className='tui-grid-container'>
              <TuiGrid
                data={tableState}
                columns={modalColumns}
                height={tuiHeight}
                onClickCheckBox={handleCheck}
                comparisonChkKey='customAudioID'
                comparisonChkList={comparisonChkList}
                rowClickCheckBox
              />
            </div>
          )}
        </div>

        <div className='footer'>
          <div className='buttons'>
            <BtnGray onClick={handleClose}>{t('닫기')}</BtnGray>
            <BtnBlue onClick={handleSave}>{t('전송')}</BtnBlue>
          </div>
        </div>
      </div>
    </Root>
  );
};

export default TableInfoMoal;
